import { cn } from '@/utils/cn'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { type ComponentPropsWithoutRef, type ElementRef, type ReactNode, forwardRef, useState } from 'react'

export const TooltipProvider = TooltipPrimitive.Provider

const TooltipTrigger = forwardRef<
  ElementRef<typeof TooltipPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TooltipPrimitive.Trigger
    ref={ref}
    type="button"
    className={cn('outline-none transition-colors', 'focus-visible:ring', 'hocus:text-accent', className)}
    {...props}
  />
))
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName

const TooltipPortal = TooltipPrimitive.Portal
TooltipPortal.displayName = TooltipPrimitive.Portal.displayName

export const tooltipContentStyles =
  'isolate z-50 max-w-[min(65ch,calc(100vw-2rem))] rounded-2xl p-3 bg-tooltip text-button-foreground font-medium text-sm'

export const TooltipContent = forwardRef<
  ElementRef<typeof TooltipPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, side = 'top', sideOffset = 4, align = 'center', alignOffset = 0, children, ...props }, ref) => (
  <TooltipPortal>
    <TooltipPrimitive.Content
      ref={ref}
      side={side}
      sideOffset={sideOffset}
      align={align}
      alignOffset={alignOffset}
      className={cn(
        tooltipContentStyles,
        'origin-[var(--radix-tooltip-content-transform-origin)]',
        'data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-[state=delayed-open]:animate-in',
        'data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:animate-out',
        'data-[side=bottom]:slide-in-from-top-2',
        'data-[side=left]:slide-in-from-right-2',
        'data-[side=right]:slide-in-from-left-2',
        'data-[side=top]:slide-in-from-bottom-2',
        className
      )}
      {...props}
    >
      {children}
      <TooltipPrimitive.Arrow className="fill-tooltip" />
    </TooltipPrimitive.Content>
  </TooltipPortal>
))

TooltipContent.displayName = TooltipPrimitive.Content.displayName

export interface TooltipProps {
  children: [ReactNode, ReactNode]
  className?: string
  tabIndex?: number
  asChild?: boolean
  onClick?: TooltipPrimitive.TooltipTriggerProps['onClick']
}

export const Tooltip = ({ children, className, tabIndex, asChild, onClick }: TooltipProps) => {
  const [open, setOpen] = useState(false)

  function handleClick(event: any) {
    onClick?.(event)
    setOpen((prevState) => !prevState)
  }

  return (
    <TooltipPrimitive.Root open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild={asChild} onClick={handleClick} className={cn(className)} tabIndex={tabIndex}>
        {children[0]}
      </TooltipTrigger>
      {children[1]}
    </TooltipPrimitive.Root>
  )
}
